import { Box, HStack, VStack } from '@chakra-ui/react';
import { useMaterialStandardId } from 'containers/Esrs/EsrsAssessment.hooks';
import {
  useGetBuDisclosureRequirementMetricsQuery,
  useGetBumfDisclosureRequirementMetricsQuery,
  useGetShortEsrsStandardQuery,
} from 'models';
import { ContentLayout, Loader, TOP_MENU_HEIGHT } from 'Molecules';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography } from 'Tokens';
import { MetricViewEnums, UNKONWN_ROUTE } from '..';
import { Structure } from '../../Assessment/Structure';
import { Metrics } from './Metrics/Metrics';
import { mfStandardRefs } from 'containers/Esrs/utils';
import { Button } from 'Atoms/Buttons';
import { ArrowLeftIcon } from 'Tokens/Icons/Direction';

export const MetricsDisclosureRequirements = () => {
  const {
    companyId = '',
    standardRef = '',
    esrsAssessmentId = '',
    reportingUnitId = '',
    disclosureRequirementRef,
    view,
    framework = '',
  } = useParams();
  const navigate = useNavigate();
  const [selectedNodeKey, setSelectedNodeKey] = useState<string>('');
  const isMFStandard = mfStandardRefs.includes(standardRef);

  const { data: standardData, loading: standardLoading } = useGetShortEsrsStandardQuery({
    variables: { reference: standardRef },
    skip: !standardRef,
  });

  const { companyAssessmentId: companyStandardId, parentAssessmentId: parentStandardId } =
    useMaterialStandardId(standardRef, esrsAssessmentId);

  const { data: normalBuData, loading } = useGetBuDisclosureRequirementMetricsQuery({
    variables: {
      standardRef: standardRef,
      companyStandardId,
      parentStandardId: parentStandardId || companyStandardId,
    },
    skip: !companyStandardId || !isMFStandard,
  });

  const { data: mfBUData, loading: mfloading } = useGetBumfDisclosureRequirementMetricsQuery({
    variables: {
      mfStandardRef: standardRef,
      materialStandardId: companyStandardId,
      parentStandardId: parentStandardId || companyStandardId,
    },
    skip: !companyStandardId || isMFStandard,
  });

  const buData = useMemo(
    () => (isMFStandard ? mfBUData : normalBuData),
    [isMFStandard, mfBUData, normalBuData]
  );

  const standard = useMemo(() => standardData?.esrsStandard, [standardData]);
  const disclosureRequirements = useMemo(
    () =>
      [...(buData?.requirements ?? [])].sort((a, b) => {
        if (a.order !== b.order) {
          return a.order - b.order;
        }
        return a.reference.localeCompare(b.reference);
      }),
    [buData]
  );

  const handleNodeSelect = (nodeKey: string) => {
    navigate(
      `/${companyId}/sustainability-reporting/${framework}/${esrsAssessmentId}/bu-standard/${standardRef}/bu/${reportingUnitId}/disclosure-requirement/${nodeKey}/${
        view ?? MetricViewEnums.dataInput
      }`
    );
    setSelectedNodeKey(nodeKey);
  };

  useEffect(() => {
    if (!disclosureRequirementRef || disclosureRequirementRef === UNKONWN_ROUTE) {
      setSelectedNodeKey(disclosureRequirements[0]?.reference);
    } else {
      setSelectedNodeKey(disclosureRequirementRef);
    }
  }, [disclosureRequirements, disclosureRequirementRef]);

  if (standardLoading || loading || mfloading) {
    return <Loader />;
  }

  return (
    <Box flexGrow="1" width="100%" display="flex" alignItems="flex-start">
      <HStack alignItems="stretch" flexGrow="1" gap="0" w="100%">
        <VStack
          height={`calc(100vh - ${TOP_MENU_HEIGHT})`}
          borderRight="1px solid"
          borderRightColor="border.decorative"
          minWidth="288px"
          maxWidth="288px"
          alignItems="start"
        >
          <Button
            size="sm"
            variant="ghost"
            aria-label={'Back'}
            leftIcon={<ArrowLeftIcon />}
            mt="8px"
            ml="8px"
            onClick={() =>
              navigate(
                `/${companyId}/sustainability-reporting/${framework}/${esrsAssessmentId}/bu-standard/${standardRef}/bu/${reportingUnitId}`
              )
            }
          >
            Back
          </Button>
          <Structure
            overflow="auto"
            w="100%"
            header={
              <VStack p="10px 16px 8px" spacing="24px" alignItems="start">
                <VStack spacing="4px" alignItems="start">
                  <Typography variant="h2">{standard?.title}</Typography>
                  <Typography variant="body" color="text.muted">
                    {standard?.reference}
                  </Typography>
                </VStack>
                <Typography variant="overline" color="text.muted" textTransform="uppercase">
                  Disclosure requirements
                </Typography>
              </VStack>
            }
            nodes={[
              ...disclosureRequirements?.map((dr) => ({
                title: dr.title,
                key: dr.reference,
                reference: dr.reference,
              })),
            ]}
            selectedNodeKey={selectedNodeKey}
            onSelectNode={handleNodeSelect}
            size="sm"
          />
        </VStack>
        <VStack height={`calc(100vh - ${TOP_MENU_HEIGHT})`} width="100%" overflow="hidden auto">
          <ContentLayout variant="inline" header={false} noHeader>
            <HStack gap="16px" position="relative">
              <Box
                height={`calc(calc(100vh - ${TOP_MENU_HEIGHT}) - 32px)`}
                overflowY="auto"
                marginRight="-16px"
                paddingRight="16px"
                w="calc(100% + 16px)"
              >
                <Metrics />
              </Box>
            </HStack>
          </ContentLayout>
        </VStack>
      </HStack>
    </Box>
  );
};
